import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormAbout = ({ titleAr, titleEn, currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    // Form States
    const [titleArState, setTitleArState] = useState("");
    const [titleEnState, setTitleEnState] = useState("");
    const [subTitleArState, setSubTitleArState] = useState("");
    const [subTitleEnState, setSubTitleEnState] = useState("");
    const [descriptionArState, setDescriptionArState] = useState("");
    const [descriptionEnState, setDescriptionEnState] = useState("");
    const [experienceYears, setExperienceYears] = useState("");
    const [experienceTitleAr, setExperienceTitleAr] = useState("");
    const [experienceTitleEn, setExperienceTitleEn] = useState("");
    const [topImage, setTopImage] = useState(null);
    const [bottomImage, setBottomImage] = useState(null);

    // Fill form data
    useEffect(() => {
        if (currentData) {
            setTitleArState(currentData?.title?.ar || "");
            setTitleEnState(currentData?.title?.en || "");
            setSubTitleArState(currentData?.sub_title?.ar || "");
            setSubTitleEnState(currentData?.sub_title?.en || "");
            setDescriptionArState(currentData?.description?.ar || "");
            setDescriptionEnState(currentData?.description?.en || "");
            setExperienceYears(currentData?.experience_years || "");
            setExperienceTitleAr(currentData?.experience_title?.ar || "");
            setExperienceTitleEn(currentData?.experience_title?.en || "");
            setTopImage(currentData?.top_image || null);
            setBottomImage(currentData?.bottom_image || null);
        }
    }, [currentData]);

    const handleTopImageChange = (e) => {
        const val = e.target.value;
        setTopImage(val);
    };

    const handleBottomImageChange = (e) => {
        const val = e.target.value;
        setBottomImage(val);
    };

    // دالة مساعدة لاستخراج اسم الصورة فقط من الرابط
    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            // تقسيم الرابط عبر "/" وأخذ العنصر الأخير
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal; // في حال كان كائن ملف (File Object) مستقبلاً
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // بناء كائن JSON متداخل ومطابق تماماً لما يتوقعه السيرفر
        const payload = {
            title: {
                ar: titleArState || titleEnState,
                en: titleEnState || titleArState
            },
            sub_title: {
                ar: subTitleArState || subTitleEnState,
                en: subTitleEnState || subTitleArState
            },
            description: {
                ar: descriptionArState || descriptionEnState,
                en: descriptionEnState || descriptionArState
            },
            experience_years: experienceYears,
            experience_title: {
                ar: experienceTitleAr || experienceTitleEn,
                en: experienceTitleEn || experienceTitleAr
            },
            top_image: getImageName(topImage),
            bottom_image: getImageName(bottomImage)
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? titleAr : titleEn}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Images Upload */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="top_image"
                            labelImage={isArabic ? "الصورة العلوية" : "Top Image"}
                            value={topImage}
                            onChange={handleTopImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="bottom_image"
                            labelImage={isArabic ? "الصورة السفلية" : "Bottom Image"}
                            value={bottomImage}
                            onChange={handleBottomImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الرئيسي (عربي)"
                                placeholder="أدخل العنوان الرئيسي بالعربي"
                                value={titleArState}
                                onChange={(e) => setTitleArState(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Main Title (English)"
                                placeholder="Enter main title in English"
                                value={titleEnState}
                                onChange={(e) => setTitleEnState(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Multilingual Sub Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي (عربي)"
                                placeholder="أدخل العنوان الفرعي بالعربي"
                                value={subTitleArState}
                                onChange={(e) => setSubTitleArState(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Sub Title (English)"
                                placeholder="Enter sub title in English"
                                value={subTitleEnState}
                                onChange={(e) => setSubTitleEnState(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Multilingual Experience Title */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان الخبرة (عربي)"
                                placeholder="أدخل عنوان الخبرة بالعربي"
                                value={experienceTitleAr}
                                onChange={(e) => setExperienceTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Experience Title (English)"
                                placeholder="Enter experience title in English"
                                value={experienceTitleEn}
                                onChange={(e) => setExperienceTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Experience Years */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "سنوات الخبرة" : "Experience Years"}
                            placeholder="e.g. 10"
                            value={experienceYears}
                            onChange={(e) => setExperienceYears(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                الوصف (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionArState}
                                onChange={(e) => setDescriptionArState(e.target.value)}
                                placeholder="أدخل الوصف بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEnState}
                                onChange={(e) => setDescriptionEnState(e.target.value)}
                                placeholder="Enter description in English"
                            />
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        sx={{ minWidth: 120 }}
                    >
                        {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAbout;
